import React, { useEffect, useMemo, useState } from "react";
import { database } from "../services/firebase";
import { ref, get } from "firebase/database";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import PremiumContentViewer from "./PremiumContentViewer";

const PREMIUM_STRUCTURE = {
  Prelims: [
    {
      key: "prlimsgs1indianpolityandgover",
      title: "Indian Polity & Governance",
      icon: "🏛️",
      desc: "Constitution, polity, governance, rights and institutions."
    },
    {
      key: "prlimsgs1economy",
      title: "Economy",
      icon: "💹",
      desc: "Macroeconomics, budgeting, inflation, growth and policy."
    },
    {
      key: "prlimsgs1geography",
      title: "Geography",
      icon: "🌍",
      desc: "Physical, Indian and world geography for Prelims."
    },
    {
      key: "prlimsgs1enviroinment",
      title: "Environment",
      icon: "🌱",
      desc: "Ecology, biodiversity, climate and environmental governance."
    },
    {
      key: "prlimsgs1science",
      title: "Science",
      icon: "🔬",
      desc: "Science and technology concepts for UPSC preparation."
    },
    {
      key: "prlimsgs1reading",
      title: "Reading",
      icon: "📘",
      desc: "Comprehension, interpretation and language reasoning."
    },
    {
      key: "prlimsgs1history",
      title: "History",
      icon: "🏺",
      desc: "Ancient, medieval and modern Indian history."
    },
    {
      key: "prlimsgs1reasoning",
      title: "Reasoning",
      icon: "🧠",
      desc: "Logical reasoning, patterns and aptitude."
    },
    {
      key: "prlimsgs1analytical",
      title: "Analytical",
      icon: "📊",
      desc: "Analytical thinking and decision-making questions."
    },
    {
      key: "prlimsgs1numeracy",
      title: "Numeracy",
      icon: "➗",
      desc: "Basic numeracy, arithmetic and interpretation."
    }
  ],

  Mains: [
    {
      key: "mainpaper1",
      title: "Paper A Indian Language",
      icon: "📝",
      desc: "Language paper practice and comprehension."
    },
    {
      key: "mainspaper2",
      title: "English",
      icon: "📚",
      desc: "English paper practice, grammar and vocabulary."
    },
    {
      key: "mainseassy3",
      title: "Mains Essay",
      icon: "✍️",
      desc: "Essay themes, constitutional, development and society."
    },
    {
      key: "mainsgs1",
      title: "GS 1",
      icon: "🏺",
      desc: "History, culture, geography and Indian society."
    },
    {
      key: "mainsgs2",
      title: "GS 2",
      icon: "🏛️",
      desc: "Governance, polity, constitution and international relations."
    },
    {
      key: "mainsgs3",
      title: "GS 3",
      icon: "🚀",
      desc: "Economy, technology, environment, disaster and security."
    },
    {
      key: "mainsgs4",
      title: "GS 4",
      icon: "⚖️",
      desc: "Ethics, integrity and aptitude."
    }
  ],

  Interview: [
    {
      key: "interview",
      title: "Interview",
      icon: "🎤",
      desc: "Personality test, mock prompts and situational responses."
    }
  ],


Law: [
  {
    key: "lawipc",
    title: "IPC Sections",
    icon: "⚖️",
    desc: "Indian Penal Code sections with explanations and UPSC focus."
  },
  { 
    key: "lawconstitution",
    title: "Constitution Articles",
    icon: "🏛️",
    desc: "Important Articles with explanations and case linkage."
  },
  {
    key: "lawjudgments",
    title: "Landmark Judgments",
    icon: "📜",
    desc: "Important Supreme Court judgments for UPSC."
  }
]






};

const getInitialExpandedState = () => {
  if (typeof window !== "undefined" && window.innerWidth < 992) {
    return {
      Prelims: false,
      Mains: false,
      Interview: false,
      Law: false
    };
  }

  return {
    Prelims: true,
    Mains: true,
    Interview: true,
     Law: true
  };
};

const PremiumDashboard = () => {
  const { user } = useContext(AuthContext);

  const [selectedPath, setSelectedPath] = useState("prlimsgs1reading");
  const [selectedTitle, setSelectedTitle] = useState("Reading");
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [loadingNode, setLoadingNode] = useState(false);
  const [nodeError, setNodeError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(getInitialExpandedState);

  // PREMIUM SUBSCRIPTION STATES
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    expired: false,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const isMobile =
    typeof window !== "undefined" ? window.innerWidth < 992 : false;

  useEffect(() => {
    if (selectedPath) {
      fetchSelectedNode(selectedPath);
    }
  }, [selectedPath]);

  useEffect(() => {
    if (user?.uid) {
      fetchSubscription();
    } else {
      setSubscriptionLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!subscriptionData?.expiryTimestamp) return;

    const interval = setInterval(() => {
      updateCountdown(subscriptionData.expiryTimestamp);
    }, 1000);

    updateCountdown(subscriptionData.expiryTimestamp);

    return () => clearInterval(interval);
  }, [subscriptionData]);

  const allNodes = useMemo(() => {
    return Object.values(PREMIUM_STRUCTURE).flat();
  }, []);

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);

      const snapshot = await get(ref(database, `subscriptions/${user.uid}`));

      if (snapshot.exists()) {
        const data = snapshot.val();
        setSubscriptionData(data);
        updateCountdown(data.expiryTimestamp);
      } else {
        setSubscriptionData(null);
      }
    } catch (error) {
      console.error("🔥 Subscription fetch error:", error);
      setSubscriptionData(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const updateCountdown = (expiryTimestamp) => {
    if (!expiryTimestamp) return;

    const now = new Date().getTime();
    const expiry = new Date(expiryTimestamp).getTime();
    const distance = expiry - now;

    if (distance <= 0) {
      setTimeLeft({
        expired: true,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((distance / (1000 * 60)) % 60);
    const seconds = Math.floor((distance / 1000) % 60);

    setTimeLeft({
      expired: false,
      days,
      hours,
      minutes,
      seconds
    });
  };

  const fetchSelectedNode = async (path) => {
    try {
      setLoadingNode(true);
      setNodeError("");

      const snapshot = await get(ref(database, path));

      if (snapshot.exists()) {
        setSelectedNodeData(snapshot.val());
      } else {
        setSelectedNodeData(null);
      }
    } catch (error) {
      console.error("🔥 Selected node fetch error:", error);
      setSelectedNodeData(null);
      setNodeError(`Failed to load content: ${error.message}`);
    } finally {
      setLoadingNode(false);
    }
  };

  const handleSidebarSelect = (path, title) => {
    setSelectedPath(path);
    setSelectedTitle(title);

    if (typeof window !== "undefined" && window.innerWidth < 992) {
      setMobileMenuOpen(false);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleGroup = (group) => {
    const mobile = typeof window !== "undefined" && window.innerWidth < 992;

    if (mobile) {
      setExpandedGroups((prev) => ({
        Prelims: false,
        Mains: false,
        Interview: false,
        Law: false,
        [group]: !prev[group]
      }));
    } else {
      setExpandedGroups((prev) => ({
        ...prev,
        [group]: !prev[group]
      }));
    }
  };

  const renderSubcategoryCards = () => {
    if (!selectedNodeData || Array.isArray(selectedNodeData)) return null;

    if (typeof selectedNodeData === "object") {
      const validEntries = Object.entries(selectedNodeData).filter(([_, value]) => {
        return Array.isArray(value) || (typeof value === "object" && value !== null);
      });

      if (validEntries.length === 0) return null;

      return (
        <div className="mb-5">
          <div className="d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-3 mb-4">
            <div>
              <h4
                className="fw-bold mb-2"
                style={{ color: "#f8fafc", fontSize: "1.2rem" }}
              >
                📂 Available Subjects / Subcategories
              </h4>
              <p style={{ color: "#94a3b8", marginBottom: 0 }}>
                Click any card below to open that content directly.
              </p>
            </div>

            <div style={pillInfoStyle}>
              {validEntries.length} Sections
            </div>
          </div>

          <div className="row g-3 g-md-4">
            {validEntries.map(([subKey, subValue], index) => {
              const subPath = `${selectedPath}/${subKey}`;
              const count = countQuestionsDeep(subValue);

              return (
                <div key={index} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                  <div
                    onClick={() => {
                      setSelectedPath(subPath);
                      setSelectedTitle(subKey);

                      if (typeof window !== "undefined" && window.innerWidth < 992) {
                        setMobileMenuOpen(false);
                      }

                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    style={cardStyle}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow =
                        "0 18px 40px rgba(0,0,0,0.28)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 30px rgba(0,0,0,0.22)";
                    }}
                  >
                    <div style={iconBoxStyle}>📘</div>

                    <h5
                      style={{
                        fontWeight: 800,
                        color: "#f8fafc",
                        marginBottom: "8px",
                        lineHeight: "1.45",
                        fontSize: "1rem"
                      }}
                    >
                      {formatTitle(subKey)}
                    </h5>

                    <p
                      style={{
                        color: "#94a3b8",
                        fontSize: "0.92rem",
                        marginBottom: "14px",
                        lineHeight: "1.65"
                      }}
                    >
                      Open this premium content set and view all answers.
                    </p>

                    <div style={successPillStyle}>
                      {count} Questions
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="container-fluid"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(56,189,248,0.08), transparent 24%), radial-gradient(circle at top right, rgba(168,85,247,0.08), transparent 26%), linear-gradient(135deg, #020617, #0f172a, #111827)",
        color: "#fff",
        padding: 0
      }}
    >
      <div className="row g-0" style={{ minHeight: "100vh" }}>
        {/* SIDEBAR */}
        <div
          className="col-12 col-lg-3 col-xl-2"
          style={{
            borderRight: "1px solid rgba(255,255,255,0.08)",
            borderBottom: isMobile ? "1px solid rgba(255,255,255,0.08)" : "none",
            background: "rgba(255,255,255,0.03)",
            backdropFilter: "blur(18px)",
            padding: isMobile ? "14px 12px" : "20px 14px",
            height: "auto",
            maxHeight: isMobile ? "unset" : "100vh",
            overflowY: "auto",
            position: "sticky",
            top: 0,
            zIndex: 20
          }}
        >
          <div className="mb-3 mb-lg-4">
            <div
              style={{
                display: "inline-block",
                padding: "8px 16px",
                borderRadius: "999px",
                background: "rgba(56,189,248,0.12)",
                border: "1px solid rgba(56,189,248,0.18)"
              }}
            >
              <span
                style={{
                  color: "#38bdf8",
                  fontWeight: 800,
                  fontSize: "12px",
                  letterSpacing: "1px"
                }}
              >
                UPSC PREMIUM
              </span>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <div>
                <h2
                  className="mb-1"
                  style={{
                    fontWeight: 900,
                    fontSize: isMobile ? "1.35rem" : "1.55rem",
                    background: "linear-gradient(90deg, #fff, #cbd5e1, #38bdf8)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}
                >
                  Dashboard
                </h2>

                {!isMobile && (
                  <p
                    style={{
                      color: "#94a3b8",
                      fontSize: "0.9rem",
                      lineHeight: "1.7",
                      marginBottom: 0
                    }}
                  >
                    Browse all premium nodes, subjects and papers directly from Firebase.
                  </p>
                )}
              </div>

              {isMobile && (
                <button
                  className="btn"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  style={{
                    borderRadius: "14px",
                    padding: "10px 14px",
                    border: "1px solid rgba(255,255,255,0.10)",
                    background: "rgba(255,255,255,0.05)",
                    color: "#f8fafc",
                    fontWeight: 700,
                    fontSize: "14px"
                  }}
                >
                  {mobileMenuOpen ? "Close" : "Browse"}
                </button>
              )}
            </div>

            {isMobile && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px 14px",
                  borderRadius: "14px",
                  background: "rgba(34,197,94,0.14)",
                  border: "1px solid rgba(34,197,94,0.18)",
                  color: "#bbf7d0",
                  fontSize: "0.88rem",
                  lineHeight: "1.6"
                }}
              >
                Tap <strong>Browse</strong> to open subjects.
              </div>
            )}
          </div>

          {(!isMobile || mobileMenuOpen) &&
            Object.entries(PREMIUM_STRUCTURE).map(([groupName, nodes]) => (
              <div key={groupName} className="mb-4">
                <div
                  onClick={() => toggleGroup(groupName)}
                  style={groupHeaderStyle}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <span
                      style={{
                        color: "#f8fafc",
                        fontWeight: 800,
                        fontSize: "0.98rem"
                      }}
                    >
                      {groupName}
                    </span>
                    <span style={{ color: "#38bdf8", fontWeight: 900 }}>
                      {expandedGroups[groupName] ? "−" : "+"}
                    </span>
                  </div>
                </div>

                {expandedGroups[groupName] && (
                  <div className="d-flex flex-column gap-2">
                    {nodes.map((node) => {
                      const isSelected = selectedPath === node.key;

                      return (
                        <div
                          key={node.key}
                          onClick={() => handleSidebarSelect(node.key, node.title)}
                          style={{
                            cursor: "pointer",
                            borderRadius: "16px",
                            padding: "12px",
                            background: isSelected
                              ? "linear-gradient(135deg, rgba(56,189,248,0.20), rgba(59,130,246,0.10))"
                              : "rgba(255,255,255,0.03)",
                            border: isSelected
                              ? "1px solid rgba(56,189,248,0.28)"
                              : "1px solid rgba(255,255,255,0.06)",
                            transition: "all 0.25s ease"
                          }}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255,255,255,0.06)",
                                fontSize: "1.05rem",
                                flexShrink: 0
                              }}
                            >
                              {node.icon}
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  color: "#f8fafc",
                                  fontWeight: isSelected ? 800 : 700,
                                  fontSize: "0.93rem",
                                  lineHeight: "1.4"
                                }}
                              >
                                {node.title}
                              </div>
                              <div
                                style={{
                                  color: "#94a3b8",
                                  fontSize: "0.78rem",
                                  marginTop: "4px",
                                  lineHeight: "1.55"
                                }}
                              >
                                {node.desc}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* MAIN CONTENT */}
        <div className="col-12 col-lg-9 col-xl-10">
          <div className="container py-4 py-md-5">
            {/* HERO */}
            <div className="mb-4 mb-md-5">
              <div style={topBadgeStyle}>
                <span
                  style={{
                    fontWeight: "700",
                    fontSize: "13px",
                    letterSpacing: "1px",
                    color: "#38bdf8"
                  }}
                >
                  LIVE FIREBASE CONTENT
                </span>
              </div>

              <h1
                className="fw-bold mt-4"
                style={{
                  fontSize: "clamp(1.8rem, 4vw, 2.6rem)",
                  lineHeight: "1.2",
                  background: "linear-gradient(90deg, #ffffff, #cbd5e1, #38bdf8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                📘 {formatTitle(selectedTitle || "Premium Dashboard")}
              </h1>

              <p
                className="mt-3"
                style={{
                  color: "#cbd5e1",
                  fontSize: "1rem",
                  maxWidth: "900px",
                  lineHeight: "1.8"
                }}
              >
                Dynamic premium content loaded directly from Firebase Realtime Database with nested node support, direct arrays, subcategory browsing and answer reveal cards.
              </p>

              {selectedPath && (
                <div style={pillInfoStyle}>
                  Path: {selectedPath}
                </div>
              )}
            </div>

            {/* PREMIUM TIMER SECTION */}
            <div className="mb-5">
              <div style={premiumCardStyle}>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-4">
                  <div>
                    <div style={premiumBadgeStyle}>
                      {timeLeft.expired ? "⚠️ PREMIUM EXPIRED" : "👑 PREMIUM ACTIVE"}
                    </div>

                    <h3
                      style={{
                        color: "#f8fafc",
                        fontWeight: 900,
                        fontSize: "clamp(1.4rem, 3vw, 2rem)",
                        marginTop: "16px",
                        marginBottom: "10px"
                      }}
                    >
                      {timeLeft.expired
                        ? "Your premium plan has ended"
                        : "Your premium access is live"}
                    </h3>

                    <p
                      style={{
                        color: "#cbd5e1",
                        fontSize: "0.98rem",
                        lineHeight: "1.8",
                        marginBottom: "12px"
                      }}
                    >
                      {subscriptionLoading
                        ? "Checking subscription details..."
                        : subscriptionData?.expiryTimestamp
                        ? `Expiry Date: ${new Date(
                            subscriptionData.expiryTimestamp
                          ).toLocaleString()}`
                        : "No subscription data found."}
                    </p>

                    {subscriptionData?.paymentTimestamp && (
                      <div style={smallMetaStyle}>
                        Started:{" "}
                        {new Date(subscriptionData.paymentTimestamp).toLocaleString()}
                      </div>
                    )}
                  </div>

                  {!subscriptionLoading && subscriptionData?.expiryTimestamp && (
                    <div className="w-100" style={{ maxWidth: "520px" }}>
                      <div className="row g-3">
                        <div className="col-3">
                          <div style={timerBoxStyle}>
                            <div style={timerValueStyle}>{timeLeft.days}</div>
                            <div style={timerLabelStyle}>Days</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div style={timerBoxStyle}>
                            <div style={timerValueStyle}>{timeLeft.hours}</div>
                            <div style={timerLabelStyle}>Hours</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div style={timerBoxStyle}>
                            <div style={timerValueStyle}>{timeLeft.minutes}</div>
                            <div style={timerLabelStyle}>Minutes</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div style={timerBoxStyle}>
                            <div style={timerValueStyle}>{timeLeft.seconds}</div>
                            <div style={timerLabelStyle}>Seconds</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* QUICK SWITCH CARDS */}
         

            {loadingNode && (
              <div className="text-center mb-5">
                <div
                  className="spinner-border"
                  role="status"
                  style={{ width: "2.8rem", height: "2.8rem", color: "#38bdf8" }}
                ></div>
                <p className="mt-4 fw-semibold fs-6 fs-md-5" style={{ color: "#e2e8f0" }}>
                  Loading premium node structure...
                </p>
              </div>
            )}

            {nodeError && (
              <div
                className="mb-4"
                style={{
                  padding: "18px 20px",
                  borderRadius: "18px",
                  background: "rgba(239,68,68,0.12)",
                  border: "1px solid rgba(239,68,68,0.22)",
                  color: "#fecaca"
                }}
              >
                {nodeError}
              </div>
            )}

           

            <PremiumContentViewer
              selectedPath={selectedPath}
              selectedTitle={selectedTitle}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* =========================
   HELPERS
========================= */
const isQuestionObject = (item) => {
  return (
    item &&
    typeof item === "object" &&
    ("question" in item || "answer" in item || "explanation" in item)
  );
};

const countQuestionsDeep = (data) => {
  if (!data) return 0;

  if (Array.isArray(data)) {
    return data.filter(isQuestionObject).length;
  }

  if (typeof data === "object") {
    return Object.values(data).reduce((acc, value) => acc + countQuestionsDeep(value), 0);
  }

  return 0;
};

const formatTitle = (text = "") => {
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const cardStyle = {
  cursor: "pointer",
  borderRadius: "22px",
  padding: "20px",
  background:
    "linear-gradient(145deg, rgba(15,23,42,0.92), rgba(17,24,39,0.88))",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 12px 30px rgba(0,0,0,0.22)",
  transition: "all 0.25s ease",
  height: "100%"
};

const iconBoxStyle = {
  width: "52px",
  height: "52px",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(56,189,248,0.12)",
  border: "1px solid rgba(56,189,248,0.18)",
  fontSize: "1.3rem",
  marginBottom: "16px"
};

const topBadgeStyle = {
  display: "inline-block",
  padding: "9px 18px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)"
};

const groupHeaderStyle = {
  cursor: "pointer",
  borderRadius: "16px",
  padding: "13px 15px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.07)",
  marginBottom: "12px"
};

const pillInfoStyle = {
  marginTop: "14px",
  display: "inline-block",
  padding: "10px 16px",
  borderRadius: "14px",
  background: "rgba(56,189,248,0.08)",
  border: "1px solid rgba(56,189,248,0.14)",
  color: "#7dd3fc",
  fontSize: "14px",
  fontWeight: 700
};

const successPillStyle = {
  display: "inline-block",
  padding: "8px 14px",
  borderRadius: "999px",
  background: "rgba(34,197,94,0.12)",
  color: "#bbf7d0",
  border: "1px solid rgba(34,197,94,0.18)",
  fontSize: "13px",
  fontWeight: 700
};

const premiumCardStyle = {
  borderRadius: "28px",
  padding: "28px",
  background:
    "linear-gradient(145deg, rgba(15,23,42,0.96), rgba(17,24,39,0.92))",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow:
    "0 18px 50px rgba(0,0,0,0.30), inset 0 0 20px rgba(255,255,255,0.02)"
};

const premiumBadgeStyle = {
  display: "inline-block",
  padding: "9px 16px",
  borderRadius: "999px",
  background: "rgba(34,197,94,0.12)",
  color: "#86efac",
  border: "1px solid rgba(34,197,94,0.22)",
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.5px"
};

const smallMetaStyle = {
  color: "#94a3b8",
  fontSize: "0.88rem",
  lineHeight: "1.7"
};

const timerBoxStyle = {
  borderRadius: "22px",
  padding: "18px 12px",
  textAlign: "center",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 8px 20px rgba(0,0,0,0.18)"
};

const timerValueStyle = {
  color: "#f8fafc",
  fontSize: "clamp(1.4rem, 4vw, 2rem)",
  fontWeight: 900,
  lineHeight: 1.1
};

const timerLabelStyle = {
  color: "#94a3b8",
  fontSize: "0.82rem",
  fontWeight: 700,
  marginTop: "6px"
};

export default PremiumDashboard;



